import { useState, useEffect } from 'react';
import { createStudent, listSchools, getUserByEmail, createUser } from '@dataconnect/generated';
import { FaBook, FaSignOutAlt, FaTimes, FaPlus, FaCheck, FaInfoCircle, FaUserGraduate, FaSchool } from 'react-icons/fa';

const StudentCreation = ({ user, onStudentsAdded, onLogout }) => {
  const [student, setStudent] = useState({ 
    name: '', gradeLevel: '', schoolId: '', subjects: ['KANNADA'] 
  });
  const [schools, setSchools] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSchools, setIsLoadingSchools] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [schoolSearch, setSchoolSearch] = useState(''); // single search string
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false); // single boolean

  const brandStyles = {
    primary: 'oklch(0.72 0.19 52)',
    secondary: 'oklch(0.93 0.08 52)',
    accent: 'oklch(0.92 0.07 148)',
    text: 'oklch(0.22 0.025 75)',
    muted: 'oklch(0.50 0.02 75)',
    input: {
      width: '100%', padding: '12px 16px', borderRadius: 14,
      border: '2px solid oklch(0.88 0.02 75)',
      fontFamily: 'Nunito Sans, sans-serif', fontSize: 16, color: 'oklch(0.22 0.025 75)',
      background: 'oklch(0.985 0.008 75)', outline: 'none', transition: 'all 0.2s',
      boxShadow: '0 2px 4px oklch(0.22 0.025 75 / 0.02)'
    },
    card: {
      background: '#fff', borderRadius: 28, padding: 32,
      boxShadow: '0 20px 50px oklch(0.22 0.025 75 / 0.08)',
      border: '1px solid oklch(0.93 0.01 75)'
    }
  };

  // Fetch schools on component mount
  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const result = await listSchools({ limit: 5000 });
        if (result.data?.schools) {
          setSchools(result.data.schools.filter(school => school.isActive));
        }
      } catch (err) {

        setError('Failed to load schools. Please refresh the page.');
      } finally {
        setIsLoadingSchools(false);
      }
    };

    fetchSchools();
  }, []);

  const updateStudent = (field, value) => {
    setStudent(prev => ({ ...prev, [field]: value }));
    
    // If we select a school from searchable dropdown, hide it
    if (field === 'schoolId') {
      setShowSchoolDropdown(false);
      const selectedSchool = schools.find(s => s.id === value);
      if (selectedSchool) {
        setSchoolSearch(selectedSchool.name);
      }
    }
  };

  const handleSchoolSearch = (value) => {
    setSchoolSearch(value);
    setShowSchoolDropdown(true);
  };

  const filteredSchools = (search) => {
    if (!search) return schools;
    const searchWords = search.toLowerCase().split(/[^a-z0-9]/i).filter(Boolean);
    
    return schools.filter(school => {
      const searchableText = (
        school.searchField || 
        `${school.name} ${school.address || ''} ${school.district || ''} ${school.state || ''}`
      ).toLowerCase();
      
      return searchWords.every(word => searchableText.includes(word));
    });
  };

  const toggleSubject = (subject) => {
    const currentSubjects = student.subjects || [];
    const hasSubject = currentSubjects.includes(subject);
    
    if (hasSubject) {
      // Remove subject if it exists
      setStudent({ ...student, subjects: currentSubjects.filter(s => s !== subject) });
    } else {
      // Add subject if it doesn't exist
      setStudent({ ...student, subjects: [...currentSubjects, subject] });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate user exists
      if (!user || !user.uid) {
        throw new Error('User not authenticated. Please log in again.');
      }

      // Validate student fields
      if (!student.name || !student.gradeLevel || !student.schoolId || !student.subjects || student.subjects.length === 0) {
        throw new Error('Please fill in all fields');
      }

      // Get or create user in database
      let userId;
      try {
        // First, try to find existing user by email
        const existingUserResult = await getUserByEmail({ email: user.email });
        if (existingUserResult.data?.users?.[0]?.id) {
          userId = existingUserResult.data.users[0].id;
        } else {
          // If user doesn't exist in database, create them
          const newUserResult = await createUser({
            name: user.displayName || user.email.split('@')[0],
            email: user.email,
            passwordHash: user.uid // Store Firebase UID as reference
          });
          userId = newUserResult.data?.user_insert?.id;
          
          if (!userId) {
            throw new Error('Failed to create user record in database');
          }
        }
      } catch (userError) {
        throw new Error('Failed to set up user account. Please try again.');
      }

      // Create student
      const result = await createStudent({
        userId: userId,
        name: student.name,
        gradeLevel: parseInt(student.gradeLevel),
        schoolId: student.schoolId,
        subjects: student.subjects
      });
      
      if (result.data?.student_insert) {
        const createdStudent = {
          id: result.data.student_insert.id,
          userId: userId,
          name: student.name,
          gradeLevel: parseInt(student.gradeLevel),
          schoolId: student.schoolId,
          subjects: student.subjects
        };
        
        // Show success message
        setSuccess(`Successfully created profile for ${createdStudent.name}!`);
        setIsLoading(false);

        // Wait a moment to show the success message, then proceed
        setTimeout(() => {
          onStudentsAdded([createdStudent]);
        }, 2000);
      } else {
        throw new Error('Failed to create student profile');
      }
    } catch (error) {
      setError(error.message || 'Failed to create student. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'oklch(0.985 0.008 75)', fontFamily: 'Nunito Sans, sans-serif', padding: '60px 24px' }}>
      {/* Main Content */}
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        {/* Form Container */}
        <div style={brandStyles.card}>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: 0 }}>
              <div style={{ 
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', 
                marginBottom: 32, paddingBottom: 16, borderBottom: `2px solid ${brandStyles.secondary}`
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ 
                    width: 36, height: 36, borderRadius: 10, background: brandStyles.primary,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#fff', fontWeight: 900, fontSize: 18
                  }}>
                    <FaSchool />
                  </div>
                  <h3 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 20, color: brandStyles.text }}>
                    Add Student Details
                  </h3>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 24 }}>
                <div>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: 14, color: brandStyles.text, marginBottom: 8 }}>
                    Student Name
                  </label>
                  <input
                    type="text"
                    style={brandStyles.input}
                    placeholder="Enter student name"
                    value={student.name}
                    onChange={(e) => updateStudent('name', e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: 14, color: brandStyles.text, marginBottom: 8 }}>
                    Grade Level
                  </label>
                  <select
                    style={{ ...brandStyles.input, appearance: 'none', cursor: 'pointer' }}
                    value={student.gradeLevel}
                    onChange={(e) => updateStudent('gradeLevel', e.target.value)}
                    required
                  >
                    <option value="">Select Grade</option>
                    {[3, 4, 5, 6, 7, 8].map(grade => (
                      <option key={grade} value={grade}>Grade {grade}</option>
                    ))}
                  </select>
                </div>

                <div style={{ gridColumn: 'span 2', position: 'relative' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: 14, color: brandStyles.text, marginBottom: 8 }}>
                    School
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      style={brandStyles.input}
                      placeholder={isLoadingSchools ? 'Loading schools...' : 'Search for your school...'}
                      value={schoolSearch || ''}
                      onChange={(e) => handleSchoolSearch(e.target.value)}
                      onFocus={() => setShowSchoolDropdown(true)}
                      disabled={isLoadingSchools}
                      required={!student.schoolId}
                    />
                    {showSchoolDropdown && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                        background: '#fff', borderRadius: 14, marginTop: 8,
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)', border: '1px solid oklch(0.88 0.02 75)',
                        maxHeight: 250, overflowY: 'auto'
                      }}>
                        {filteredSchools(schoolSearch).length > 0 ? (
                          filteredSchools(schoolSearch).map(school => (
                            <div
                              key={school.id}
                              onClick={() => updateStudent('schoolId', school.id)}
                              style={{
                                padding: '16px', cursor: 'pointer', fontSize: 14,
                                borderBottom: '1px solid oklch(0.97 0.008 75)',
                                background: student.schoolId === school.id ? brandStyles.secondary : 'transparent',
                                color: brandStyles.text, transition: 'background 0.2s',
                                display: 'flex', flexDirection: 'column', gap: 4
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.background = 'oklch(0.97 0.008 75)'}
                              onMouseLeave={(e) => e.currentTarget.style.background = student.schoolId === school.id ? brandStyles.secondary : 'transparent'}
                            >
                              <div style={{ fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', gap: 8 }}>
                                <FaSchool style={{ color: brandStyles.primary, opacity: 0.7 }} />
                                {school.name}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', paddingLeft: 24 }}>
                                {school.address && (
                                  <div style={{ fontSize: 12, color: brandStyles.muted, lineHeight: 1.4 }}>
                                    {school.address}
                                  </div>
                                )}
                                <div style={{ fontSize: 11, color: brandStyles.primary, fontWeight: 700, marginTop: 2, textTransform: 'uppercase', letterSpacing: '0.02em' }}>
                                  {school.district}{school.district && school.state ? ' • ' : ''}{school.state}
                                </div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{ padding: '16px', textAlign: 'center', color: brandStyles.muted, fontSize: 14 }}>
                            No schools found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Hidden input for form validation */}
                  <input type="hidden" value={student.schoolId} required />
                </div>

                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ display: 'block', fontWeight: 700, fontSize: 14, color: brandStyles.text, marginBottom: 12 }}>
                    Subjects
                  </label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {['KANNADA'].map(subject => (
                      <div key={subject} style={{ 
                        padding: '10px 20px', borderRadius: 12, background: brandStyles.secondary,
                        border: `2px solid ${brandStyles.primary}`, color: brandStyles.primary,
                        fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8
                      }}>
                        <FaCheck /> {subject.charAt(0) + subject.slice(1).toLowerCase()}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {error && (
              <div style={{ 
                background: 'oklch(0.95 0.02 20 / 0.1)', border: '1px solid #ef4444', 
                color: '#ef4444', padding: 16, borderRadius: 16, marginTop: 24,
                textAlign: 'center', fontWeight: 600, fontSize: 14
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{ 
                background: 'oklch(0.95 0.05 148 / 0.1)', border: '1px solid oklch(0.50 0.17 148)', 
                color: 'oklch(0.50 0.17 148)', padding: 16, borderRadius: 16, marginTop: 24,
                textAlign: 'center', fontWeight: 600, fontSize: 14
              }}>
                {success}
              </div>
            )}

            <div style={{ 
              marginTop: 40, display: 'flex', flexDirection: 'column', gap: 16 
            }}>
              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%', padding: '16px', borderRadius: 16,
                  background: brandStyles.primary, color: '#fff', border: 'none',
                  fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18,
                  cursor: isLoading ? 'wait' : 'pointer', transition: 'all 0.2s',
                  boxShadow: '0 8px 24px oklch(0.72 0.19 52 / 0.3)'
                }}
              >
                {isLoading ? 'Creating Profile...' : `Create Profile →`}
              </button>
            </div>
          </form>
        </div>

        {/* Info Card */}
        <div style={{ 
          marginTop: 48, background: 'linear-gradient(135deg, oklch(0.93 0.08 52) 0%, oklch(0.92 0.07 148) 100%)',
          borderRadius: 28, padding: 32, display: 'flex', gap: 24, alignItems: 'center'
        }}>
          <div style={{ 
            width: 56, height: 56, borderRadius: 16, background: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: brandStyles.primary, fontSize: 24, flexShrink: 0
          }}>
            <FaInfoCircle />
          </div>
          <div>
            <h4 style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 800, fontSize: 18, color: brandStyles.text, marginBottom: 4 }}>
              Why create profiles? 🚀
            </h4>
            <p style={{ fontSize: 14, color: brandStyles.muted, lineHeight: 1.5 }}>
              Each child gets their own personalized dashboard, tracking their progress across chapters and activities tailored to their school syllabus.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentCreation;
